import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { CheckCircle2, MessageCircle } from "lucide-react";

const USER_TYPES = ["Student", "Fresher", "Job Seeker", "Working Professional", "College", "Employer", "Other"];
const INTEREST = ["15-Day Program", "30-Day Program", "45-Day Program", "Resume Guidance", "Career Counselling", "College Partnership", "Employer Partnership"];

export default function Contact({ site }) {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", user_type: "Student", enquiry_type: "Career Counselling", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const wa = site?.whatsapp || "918824207033";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Enquiry submitted. We'll be in touch soon!");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#0B132B] text-white overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.28em] uppercase text-slate-400">Get in touch</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-tight">Ready to Become <span className="text-metallic">Hire Ready?</span></h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-md">
            Take the first step toward better communication, stronger interview performance and greater career confidence.
          </p>
          <div className="mt-8 space-y-3 text-sm text-slate-300">
            <div>📞 {site?.phone}</div>
            <div>📍 {site?.location}</div>
            <div>✉️ {site?.email}</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="rounded-3xl glass-dark p-8 sm:p-10 text-center">
              <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
              <div className="mt-4 font-display text-2xl">Enquiry received</div>
              <p className="mt-2 text-slate-300 text-sm">Thanks — we'll reach out shortly. Want a faster response?</p>
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"
                 className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white text-sm font-medium">
                <MessageCircle size={16} /> Continue on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} data-testid={HOME.contactForm} className="rounded-3xl glass-dark p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" required value={form.full_name} onChange={upd("full_name")} testid="contact-fullname" />
                <Field label="Phone Number" required value={form.phone} onChange={upd("phone")} testid="contact-phone" />
                <Field label="Email" type="email" required value={form.email} onChange={upd("email")} testid="contact-email" />
                <Select label="Current Status" value={form.user_type} onChange={upd("user_type")} options={USER_TYPES} testid="contact-usertype" />
                <div className="sm:col-span-2">
                  <Select label="Interested In" value={form.enquiry_type} onChange={upd("enquiry_type")} options={INTEREST} testid="contact-interest" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-widest text-slate-400">Message</span>
                    <textarea rows={4} value={form.message} onChange={upd("message")} data-testid="contact-message"
                              className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-white/30 focus:outline-none text-sm text-white placeholder:text-slate-500" />
                  </label>
                </div>
              </div>
              <button disabled={submitting} data-testid={HOME.contactSubmit}
                      className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0B132B] text-sm font-semibold disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-400">{label}</span>
      <input {...props} data-testid={testid}
             className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-white/30 focus:outline-none text-sm text-white placeholder:text-slate-500" />
    </label>
  );
}
function Select({ label, options, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-400">{label}</span>
      <select {...props} data-testid={testid}
              className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-white/30 focus:outline-none text-sm text-white">
        {options.map((o) => <option key={o} value={o} className="bg-[#0B132B]">{o}</option>)}
      </select>
    </label>
  );
}
